import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { colors, typography } from '../styles/theme';
import { authInputAutofillSx } from '../Access/styles/authPanel.styles';
import { toast } from './Toast';
import { getUserLotUnits } from '../services/userLotsService';
import { getLotUserContent } from '../services/lotUserContentService';
import {
  getUserLotShare,
  shareUserLot,
  unshareUserLot,
} from '../services/userLotsShareService';
import { getUnitDisplayTitle } from '../Map/utils/userLotUnits';
import { buildLotUserContentSharePreview } from '../Map/utils/lotUserContentSharePreview';
import { resolveSpaceGuestWriteError } from '../Map/utils/spaceMirrorLots';
import { formatUserLotCommercial } from '../Map/utils/userLotFormUtils';
import { useShareDialogBrandsOverlap } from './BrandsModal/BrandsModalContext';
import { getMyBrands } from '../services/brandsService';
import ShareTemplateSection, {
  SHARE_TEMPLATE_DEFAULT,
  normalizeShareBrandId,
  normalizeShareTemplate,
  readShareApiError,
  resolveShareAppearance,
} from './ShareTemplateSection';
import {
  getShareDialogFooterScrollHintSx,
  useShareDialogScrollHint,
} from './shareDialogScrollHint';
import { useMetaShareCutGuard } from '../MetaAds/components/MetaShareCutDialog';
import { forgetMetaAutoShare } from '../MetaAds/metaCreateShare';
import {
  getShareCancelButtonSx,
  getShareDangerButtonSx,
  getSharePrimaryButtonSx,
} from './shareDialogButtonSx';

const BG_PAPER = '#1A1A1A';
const BG_ELEVATED = '#242424';
const BG_UNITS = '#1C1C1C';
const BORDER = '#383838';
const TEXT_PRIMARY = '#F9FAFB';
const TEXT_SECONDARY = '#ABABAB';
const TEXT_MUTED = 'rgba(255,255,255,0.45)';
const DANGER = '#EE5757';
const CHIP_NONE_COLOR = '#D4D4D4';

const SHARE_SECTION_SHELL_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: `1px solid ${BORDER}`,
  borderRadius: '8px',
  p: 1.5,
};

const SHARE_EMAIL_INPUT_SX = {
  flex: 1,
  minWidth: 140,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: TEXT_PRIMARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  padding: '2px 0',
  ...authInputAutofillSx,
};

const SHARE_DIALOG_MAX_HEIGHT = '80vh';

const SHARE_INVITE_LIST_MAX_HEIGHT = 160;

const SHARE_DIALOG_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.10) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
};

const SHARE_DIALOG_PAPER_SX = {
  bgcolor: BG_PAPER,
  borderRadius: '12px',
  overflow: 'hidden',
  maxHeight: SHARE_DIALOG_MAX_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
};

const SHARE_DIALOG_CONTENT_SX = {
  px: 2.5,
  pt: 2,
  pb: 1,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  ...SHARE_DIALOG_SCROLLBAR_SX,
};

const SHARE_DIALOG_LIST_META_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  flexShrink: 0,
  px: 2.5,
  pt: 2,
  pb: 0.5,
  bgcolor: BG_PAPER,
};

const INVITE_LIST_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  maxHeight: SHARE_INVITE_LIST_MAX_HEIGHT,
  overflowY: 'auto',
  pr: 0.5,
  ...SHARE_DIALOG_SCROLLBAR_SX,
  '&::-webkit-scrollbar': { width: 5 },
};

function buildSharedUserLotUrl(shareToken) {
  return `${window.location.origin}/#/shared-user-lot/${encodeURIComponent(shareToken)}`;
}

function buildSharedLotUrl(lotId) {
  return `${window.location.origin}/#/shared-lot/${encodeURIComponent(lotId)}`;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function normalizeEmail(raw) {
  return String(raw || '').trim().toLowerCase();
}

/** Iniciales a partir del local-part del email (ej. maria.garcia → MG). */
function getEmailInitials(email) {
  const local = String(email || '').split('@')[0] || '';
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  const clean = local.replace(/[^a-zA-Z0-9]/g, '');
  if (clean.length >= 2) return clean.slice(0, 2).toUpperCase();
  if (clean.length === 1) return clean.toUpperCase();
  return '?';
}

function sameEmailSet(a = [], b = []) {
  if (a.length !== b.length) return false;
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  return left.every((value, idx) => value === right[idx]);
}

function sameIdSet(a = [], b = []) {
  if (a.length !== b.length) return false;
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  return left.every((value, idx) => value === right[idx]);
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  }
}

function formatUnitMeta(unit) {
  return formatUserLotCommercial(unit);
}

function getUnitId(unit) {
  return unit?.id || unit?.user_lot_id || null;
}

const skTextSx = { bgcolor: 'rgba(255,255,255,0.10)', borderRadius: 1 };

const skFooterButtonSx = {
  bgcolor: 'rgba(255,255,255,0.10)',
  borderRadius: '8px',
  flexShrink: 0,
};

function AccessModeCard({ selected, icon, title, description, onClick, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.75,
        p: 1.5,
        m: 0,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '8px',
        border: selected ? `1.5px solid ${colors.primary}` : `1px solid ${BORDER}`,
        bgcolor: selected ? 'rgba(72,146,145,0.12)' : BG_ELEVATED,
        color: 'inherit',
        font: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        opacity: disabled ? 0.6 : 1,
        '&:hover': disabled ? undefined : {
          borderColor: selected ? colors.primary : 'rgba(255,255,255,0.28)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ color: selected ? colors.primaryLight : TEXT_SECONDARY, display: 'flex' }}>
          {icon}
        </Box>
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ color: TEXT_MUTED, fontSize: '11px', lineHeight: 1.35 }}>
        {description}
      </Typography>
    </Box>
  );
}


/**
 * Modal multi-paso para compartir un lote + unidades funcionales.
 * Paso 1: selección de unidades (el lote siempre va incluido).
 * Paso 2 (con UFs seleccionadas o share activo): acceso (público/privado) + generar enlace.
 * Solo parcela sin info privada compartible: enlace público directo /#/shared-lot/{lotId}.
 * Solo parcela con info privada marcada para compartir: flujo shareUserLot (JWT).
 */
/**
 * Compartir un lote (+ UFs). `ownerUserId` (espacio ajeno, F3): el invitado
 * comparte el lote del owner. UF, contenido y config se leen con
 * `?owner_user_id=`; el POST reenvía la config completa con `owner_user_id`
 * en el body y la apariencia del owner fija (sin las marcas del invitado).
 * Lote sin share activo (404) = no compartido. Sin `ownerUserId` → igual que
 * siempre. Ver docs/maps/espacios-invite.md.
 */
export default function ShareLotDialog({
  open,
  lotId,
  lotName = '',
  provinceName = '',
  departmentName = '',
  initialUnits = null,
  initialSelectedUnitIds = null,
  ownerUserId = null,
  onClose,
}) {
  const spaceOwner = ownerUserId ? String(ownerUserId).trim() : '';
  const isSpaceGuest = Boolean(spaceOwner);
  const brandsOverlap = useShareDialogBrandsOverlap();
  const [step, setStep] = useState('select'); // 'select' | 'access' | 'lotOnly'
  const [units, setUnits] = useState([]);
  const [lotOnlyMode, setLotOnlyMode] = useState(false);
  const [lotUserContent, setLotUserContent] = useState(null);
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);
  const [originalUnitIds, setOriginalUnitIds] = useState([]);

  const [accessMode, setAccessMode] = useState('public');
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [emails, setEmails] = useState([]);
  const [originalEmails, setOriginalEmails] = useState([]);
  /** Notificar invitados al dar/actualizar acceso (off por defecto). */
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyMessageError, setNotifyMessageError] = useState('');

  const [loading, setLoading] = useState(false);
  const [shareToken, setShareToken] = useState('');
  const [linkStatus, setLinkStatus] = useState('none'); // none | active | inactive
  const [revokedShareUrl, setRevokedShareUrl] = useState('');
  const [savedAccessMode, setSavedAccessMode] = useState(null);
  const [shareTemplate, setShareTemplate] = useState(SHARE_TEMPLATE_DEFAULT);
  const [savedShareTemplate, setSavedShareTemplate] = useState(SHARE_TEMPLATE_DEFAULT);
  /** `null` = seguí la marca principal de la empresa. */
  const [brandId, setBrandId] = useState(null);
  const [savedBrandId, setSavedBrandId] = useState(null);
  /** Espacio ajeno: se reenvía tal cual para no pisar la config del owner. */
  const [savedInquiryEmailSource, setSavedInquiryEmailSource] = useState(null);
  /** Espacio ajeno: sin la config del owner no se guarda (pisaría su marca). */
  const [guestConfigMissing, setGuestConfigMissing] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const inputRef = useRef(null);
  // Meta (t1099u): antes de cortar un link público con campañas de Terrio que llevan ahí, confirmación
  // con «Pausar y seguir». Solo el dueño en su espacio (el invitado no pausa campañas del dueño).
  const { confirmCut, guardDialog } = useMetaShareCutGuard({ enabled: !isSpaceGuest });

  const cadastralLabel = lotName || lotId || '—';
  const locationLabel = [provinceName, departmentName].filter(Boolean).join(' · ') || '—';
  const hasShareablePrivateContent = useMemo(
    () => buildLotUserContentSharePreview(lotUserContent).length > 0,
    [lotUserContent],
  );
  const unitCountLabel = selectedUnitIds.length > 0
    ? `${selectedUnitIds.length} unidad${selectedUnitIds.length !== 1 ? 'es' : ''}`
    : null;
  const selectionSummary = unitCountLabel
    ? (hasShareablePrivateContent
      ? `${unitCountLabel} e información privada del lote`
      : unitCountLabel)
    : (hasShareablePrivateContent
      ? 'Lote e información privada del lote'
      : 'Solo lote');
  const lotOnlyUrl = useMemo(
    () => (lotId ? buildSharedLotUrl(lotId) : ''),
    [lotId],
  );
  const shareUrl = useMemo(
    () => (shareToken ? buildSharedUserLotUrl(shareToken) : ''),
    [shareToken],
  );

  const isInitializing = loading || (
    open && !lotOnlyMode && step === 'select' && units.length === 0 && !error
  );
  const isPrivate = accessMode === 'restricted';
  const linkMatchesTab = savedAccessMode === accessMode;
  const effectiveLinkStatus = linkMatchesTab ? linkStatus : 'none';
  const hasActiveLink = effectiveLinkStatus === 'active' && Boolean(shareToken);
  const displayLinkUrl = hasActiveLink
    ? shareUrl
    : (effectiveLinkStatus === 'inactive' ? revokedShareUrl : '');
  const scrollHintKey = [
    open,
    step,
    isPrivate,
    isInitializing,
    emails.length,
    shareTemplate,
    effectiveLinkStatus,
  ].join('|');
  const { contentRef, canScrollMore } = useShareDialogScrollHint(scrollHintKey);
  const allowlistDirty = !sameEmailSet(emails, originalEmails);
  const unitsDirty = !sameIdSet(selectedUnitIds, originalUnitIds);
  const templateDirty = shareTemplate !== savedShareTemplate
    || brandId !== savedBrandId;
  const privatePrimaryLabel = (hasActiveLink || (linkMatchesTab && originalEmails.length > 0))
    ? 'Actualizar accesos'
    : 'Dar acceso';
  const privatePrimaryDisabled = privatePrimaryLabel === 'Dar acceso'
    ? emails.length === 0
    : !allowlistDirty && !unitsDirty && !templateDirty;
  const guestBlocked = isSpaceGuest && guestConfigMissing;

  const resetState = () => {
    setStep('select');
    setUnits([]);
    setLotOnlyMode(false);
    setLotUserContent(null);
    setSelectedUnitIds([]);
    setOriginalUnitIds([]);
    setAccessMode('public');
    setEmailInput('');
    setEmailInputError('');
    setEmails([]);
    setOriginalEmails([]);
    setNotifyUsers(false);
    setNotifyMessage('');
    setNotifyMessageError('');
    setLoading(true);
    setShareToken('');
    setLinkStatus('none');
    setRevokedShareUrl('');
    setSavedAccessMode(null);
    setShareTemplate(SHARE_TEMPLATE_DEFAULT);
    setSavedShareTemplate(SHARE_TEMPLATE_DEFAULT);
    setBrandId(null);
    setSavedBrandId(null);
    setSavedInquiryEmailSource(null);
    setGuestConfigMissing(false);
    setSubmitting(false);
    setError('');
  };

  useEffect(() => {
    if (!open) return;
    resetState();
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !lotId) return undefined;

    setLoading(true);
    setError('');

    (async () => {
      try {
        const [unitsRes, shareRes, contentRes] = await Promise.all(isSpaceGuest
          ? [
            getUserLotUnits(lotId, { ownerUserId: spaceOwner }),
            getUserLotShare({ lotId, ownerUserId: spaceOwner }),
            getLotUserContent(lotId, { ownerUserId: spaceOwner }),
          ]
          : [
            getUserLotUnits(lotId),
            getUserLotShare({ lotId }),
            getLotUserContent(lotId),
          ]);
        if (cancelled) return;

        setLotUserContent(contentRes._httpOk ? contentRes.data : null);
        const contentData = contentRes._httpOk ? contentRes.data : null;
        const hasShareablePrivate = buildLotUserContentSharePreview(contentData).length > 0;

        let nextUnits = Array.isArray(unitsRes?.data) ? unitsRes.data : [];
        if (!nextUnits.length && Array.isArray(initialUnits) && initialUnits.length) {
          nextUnits = initialUnits;
        }
        setUnits(nextUnits);

        const isLotOnly = nextUnits.length === 0;
        setLotOnlyMode(isLotOnly);

        const forcedIds = (Array.isArray(initialSelectedUnitIds) ? initialSelectedUnitIds : [])
          .filter(Boolean)
          .map(String)
          .filter((id) => nextUnits.some((u) => String(getUnitId(u)) === id));

        let preselectedIds = [];
        let hasActiveShare = false;
        if (shareRes?._httpStatus === 404) {
          setLinkStatus('none');
          setSavedAccessMode(null);
        } else if (!shareRes?._httpOk) {
          setError(
            (isSpaceGuest && resolveSpaceGuestWriteError(shareRes?._httpStatus, spaceOwner))
            || 'No pudimos cargar la configuración. Intentalo de nuevo.',
          );
          if (isSpaceGuest) setGuestConfigMissing(true);
        } else {
          const data = shareRes?.data || shareRes;
          const token = data?.share_token || data?.shareToken || '';
          const isShared = data?.is_shared !== false && Boolean(token);
          const mode = data?.access_mode || data?.accessMode || 'public';
          const normalizedMode = mode === 'restricted' ? 'restricted' : 'public';
          const allowlist = Array.isArray(data?.allowlist) ? data.allowlist : [];
          const sharedIds = Array.isArray(data?.user_lot_ids)
            ? data.user_lot_ids.filter(Boolean).map(String)
            : [];

          preselectedIds = sharedIds.filter((id) => (
            nextUnits.some((u) => String(getUnitId(u)) === String(id))
          ));

          if (isShared && token) {
            hasActiveShare = true;
            setShareToken(token);
            setLinkStatus('active');
            setRevokedShareUrl('');
            setSavedAccessMode(normalizedMode);
          } else if (token) {
            setShareToken('');
            setLinkStatus('inactive');
            setRevokedShareUrl(buildSharedUserLotUrl(token));
            setSavedAccessMode(normalizedMode);
          } else {
            setShareToken('');
            setLinkStatus('none');
            setRevokedShareUrl('');
            setSavedAccessMode(null);
          }

          setAccessMode(normalizedMode);
          const nextTemplate = normalizeShareTemplate(data?.share_template || data?.shareTemplate);
          setShareTemplate(nextTemplate);
          setSavedShareTemplate(nextTemplate);
          const nextBrandId = normalizeShareBrandId(data?.brand_id);
          setBrandId(nextBrandId);
          setSavedBrandId(nextBrandId);
          setSavedInquiryEmailSource(data?.inquiry_email_source ?? null);
          if (normalizedMode === 'restricted') {
            const preloadedEmails = allowlist
              .filter((x) => x?.type === 'email' && x?.value)
              .map((x) => String(x.value).toLowerCase());
            const uniqueEmails = Array.from(new Set(preloadedEmails));
            setEmails(uniqueEmails);
            setOriginalEmails(uniqueEmails);
          } else {
            setEmails([]);
            setOriginalEmails([]);
          }
        }

        if (isLotOnly) {
          setSelectedUnitIds([]);
          setOriginalUnitIds([]);
          setStep(hasShareablePrivate || hasActiveShare ? 'access' : 'lotOnly');
        } else {
          const nextSelectedIds = forcedIds.length > 0 ? forcedIds : preselectedIds;
          setSelectedUnitIds(nextSelectedIds);
          setOriginalUnitIds(preselectedIds);
          if (hasActiveShare || forcedIds.length > 0) {
            setStep('access');
          }
        }
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError('No pudimos cargar la configuración. Intentalo de nuevo.');
        if (isSpaceGuest) setGuestConfigMissing(true);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  // initialUnits / initialSelectedUnitIds: fallback y preselección al abrir desde detalle de UF
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lotId, initialSelectedUnitIds, spaceOwner]);

  const toggleUnit = (unitId) => {
    setSelectedUnitIds((prev) => (
      prev.includes(unitId)
        ? prev.filter((id) => id !== unitId)
        : [...prev, unitId]
    ));
    setError('');
  };

  const tryCommitEmail = () => {
    const value = normalizeEmail(emailInput);
    if (!value) return;
    if (!isValidEmail(value)) {
      setEmailInputError('El email no tiene un formato válido.');
      return;
    }
    if (emails.includes(value)) {
      setEmailInputError('Ese email ya fue agregado.');
      return;
    }
    setEmails((prev) => [...prev, value]);
    setEmailInput('');
    setEmailInputError('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      tryCommitEmail();
    }
    if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
      setEmailInputError('');
    }
  };

  const handleEmailChange = (e) => {
    const raw = e.target.value;
    if (raw.includes(',') || raw.includes(';')) {
      const parts = raw.split(/[,;]+/).map(normalizeEmail).filter(Boolean);
      const valid = [];
      const invalid = [];
      parts.forEach((p) => {
        if (!isValidEmail(p)) { invalid.push(p); return; }
        if (!emails.includes(p)) valid.push(p);
      });
      if (valid.length) setEmails((prev) => [...prev, ...valid]);
      if (invalid.length) {
        setEmailInput(invalid.join(', '));
        setEmailInputError(`Algunos emails no son válidos: ${invalid.join(', ')}`);
      } else {
        setEmailInput('');
        setEmailInputError('');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }
    setEmailInput(raw);
    if (emailInputError) setEmailInputError('');
  };

  const handleEmailBlur = () => {
    if (emailInput.trim()) tryCommitEmail();
  };

  const handleRemoveInvitee = (email) => {
    const previous = emails;
    setEmails((prev) => prev.filter((x) => x !== email));
    setEmailInputError('');
    toast.info('Se quitó al usuario de la lista.', {
      actionLabel: 'Deshacer',
      onAction: () => setEmails(previous),
      duration: 5000,
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleCopyLotOnlyLink = async () => {
    if (!lotOnlyUrl) return;
    const copied = await copyToClipboard(lotOnlyUrl);
    toast.success(copied ? '¡Link copiado al portapapeles!' : 'Link listo para compartir');
  };

  const handleCopyLotOnlyLinkAndClose = async () => {
    await handleCopyLotOnlyLink();
    handleClose();
  };

  const handleCopyLink = async () => {
    if (!displayLinkUrl || effectiveLinkStatus === 'inactive') return;
    const copied = await copyToClipboard(displayLinkUrl);
    toast.success(copied ? '¡Link copiado al portapapeles!' : 'Link generado correctamente');
  };

  const doSubmit = async () => {
    if (!lotId) return;

    if (emailInput.trim()) {
      const value = normalizeEmail(emailInput);
      if (!isValidEmail(value)) {
        setEmailInputError('El email no tiene un formato válido.');
        return;
      }
      if (!emails.includes(value)) setEmails((prev) => [...prev, value]);
      setEmailInput('');
      setEmailInputError('');
      await new Promise((r) => setTimeout(r, 0));
    }

    setError('');
    setNotifyMessageError('');
    setSubmitting(true);
    try {
      const finalEmails = emailInput.trim()
        ? [...emails, normalizeEmail(emailInput)].filter((v, i, a) => a.indexOf(v) === i)
        : emails;

      const updatingPrivateAccess = accessMode === 'restricted'
        && (hasActiveLink || originalEmails.length > 0);

      if (accessMode === 'restricted' && finalEmails.length === 0 && !updatingPrivateAccess) {
        setError('Agregá al menos un email para poder continuar.');
        setSubmitting(false);
        return;
      }

      const allowlist =
        accessMode === 'restricted'
          ? finalEmails.map((email) => ({ type: 'email', value: email }))
          : undefined;

      // Solo se notifica a emails nuevos (no estaban en la allowlist guardada).
      const originalSet = new Set(originalEmails.map((e) => normalizeEmail(e)));
      const newInviteEmails = finalEmails.filter((e) => !originalSet.has(normalizeEmail(e)));

      const inviteMessage = notifyMessage.trim().slice(0, 2000);
      const wantsNotify = accessMode === 'restricted' && notifyUsers;
      if (wantsNotify && !inviteMessage) {
        setNotifyMessageError('Escribí un mensaje para notificar a los usuarios.');
        setSubmitting(false);
        return;
      }
      if (wantsNotify && newInviteEmails.length === 0) {
        setNotifyMessageError('Solo se notifica a personas nuevas. Agregá al menos un email nuevo.');
        setSubmitting(false);
        return;
      }
      const shouldNotify = wantsNotify && newInviteEmails.length > 0;

      const userLotIdsToShare = lotOnlyMode ? [] : selectedUnitIds;

      if (lotOnlyMode && !hasShareablePrivateContent) {
        setError('Marcá al menos un ítem privado para compartir antes de generar el enlace.');
        setSubmitting(false);
        return;
      }

      // Link público activo que se corta para los anuncios: pasa a privado (todas las unidades del
      // lote) o salen unidades del link (esas).
      const publicActive = linkStatus === 'active' && savedAccessMode === 'public';
      if (publicActive) {
        const removedIds = originalUnitIds.filter((id) => !userLotIdsToShare.includes(id));
        let goOn = true;
        if (accessMode === 'restricted') {
          goOn = await confirmCut({ userLotIds: units.map(getUnitId).filter(Boolean).map(String) }, 'restrict');
        } else if (removedIds.length > 0) {
          goOn = await confirmCut({ userLotIds: removedIds.map(String) }, 'remove_units');
        }
        if (!goOn) {
          setSubmitting(false);
          return;
        }
      }

      // Espacio ajeno: la apariencia es la del owner (la del share activo; sin
      // share activo, Terrio, como arranca el dueño), sin mirar las marcas del invitado.
      let appearance = { shareTemplate, brandId };
      if (!isSpaceGuest) {
        const brandsRes = await getMyBrands();
        appearance = resolveShareAppearance(
          shareTemplate,
          brandId,
          brandsRes.ok ? brandsRes.brands : [],
        );
      }

      const res = await shareUserLot({
        lot_id: lotId,
        user_lot_ids: userLotIdsToShare,
        scope: 'all',
        access_mode: accessMode,
        allowlist,
        share_template: appearance.shareTemplate,
        brand_id: appearance.brandId,
        ...(isSpaceGuest
          ? { inquiry_email_source: savedInquiryEmailSource, ownerUserId: spaceOwner }
          : {}),
        ...(shouldNotify
          ? {
              send_invite: true,
              invite_message: inviteMessage,
              invite_emails: newInviteEmails,
            }
          : {}),
      });

      const token = res?.data?.share_token;
      if (!token) {
        setError(
          (isSpaceGuest && resolveSpaceGuestWriteError(res?._httpStatus, spaceOwner))
          || readShareApiError(
            res,
            'No se pudo guardar la configuración. Intentalo de nuevo.',
          ),
        );
        setSubmitting(false);
        return;
      }

      // El dueño guardó el share a mano: ya no es «solo para el anuncio» (Meta, paso 3).
      if (!isSpaceGuest) forgetMetaAutoShare({ scope: 'lot', id: lotId });
      setShareToken(token);
      setLinkStatus('active');
      setRevokedShareUrl('');
      setSavedAccessMode(accessMode);
      setSavedShareTemplate(
        normalizeShareTemplate(res?.data?.share_template || shareTemplate),
      );
      // Con `default` el backend limpia la marca: el estado local se alinea con
      // lo que quedó guardado en vez de conservar la última elección.
      const nextSavedBrandId = Object.prototype.hasOwnProperty.call(res?.data || {}, 'brand_id')
        ? normalizeShareBrandId(res.data.brand_id)
        : brandId;
      setSavedBrandId(nextSavedBrandId);
      setBrandId(nextSavedBrandId);
      setOriginalUnitIds([...userLotIdsToShare]);
      if (accessMode === 'restricted') {
        setOriginalEmails([...finalEmails]);
        setEmails([...finalEmails]);

        if (!updatingPrivateAccess) {
          void copyToClipboard(buildSharedUserLotUrl(token));
        }

        if (updatingPrivateAccess && shouldNotify) {
          toast.success('Se notificó a las nuevas personas con acceso.', {
            title: 'Accesos actualizados.',
          });
        } else if (updatingPrivateAccess) {
          toast.success('Ya podés compartir el enlace con las nuevas personas.', {
            title: 'Accesos actualizados.',
          });
        } else if (shouldNotify) {
          toast.success('Se notificó a las personas y el enlace privado quedó activo.', {
            title: 'Accesos asignados.',
          });
        } else {
          toast.success('Se generó un enlace privado, y está disponible para compartir.', {
            title: 'Accesos asignados.',
          });
        }

        const invite = res?.data?.invite;
        if (Array.isArray(invite?.failed) && invite.failed.length > 0) {
          toast.warning(
            `No se pudo notificar a: ${invite.failed.join(', ')}`
          );
        }

        if (shouldNotify) {
          setNotifyUsers(false);
          setNotifyMessage('');
          setNotifyMessageError('');
        }
      } else {
        setOriginalEmails([]);
        setEmails([]);
        const url = buildSharedUserLotUrl(token);
        const copied = await copyToClipboard(url);
        toast.success(copied ? '¡Link copiado al portapapeles!' : 'Link generado correctamente');
      }
    } catch (e) {
      setError('Ocurrió un error al guardar. Intentalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting || isInitializing || guestBlocked) return;
    if (accessMode === 'restricted') {
      if (privatePrimaryDisabled) return;
      if (privatePrimaryLabel === 'Dar acceso' && emails.length === 0 && !emailInput.trim()) {
        setError('Agregá al menos un email para poder continuar.');
        setEmailInputError('Agregá al menos un email.');
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      if (notifyUsers && !notifyMessage.trim()) {
        setNotifyMessageError('Escribí un mensaje para notificar a los usuarios.');
        return;
      }
      const pendingEmails = emailInput.trim()
        ? [...emails, normalizeEmail(emailInput)].filter((v, i, a) => a.indexOf(v) === i)
        : emails;
      const originalSet = new Set(originalEmails.map((e) => normalizeEmail(e)));
      const hasNewInvitees = pendingEmails.some((e) => !originalSet.has(normalizeEmail(e)));
      if (notifyUsers && !hasNewInvitees) {
        setNotifyMessageError('Solo se notifica a personas nuevas. Agregá al menos un email nuevo.');
        return;
      }
      await doSubmit();
      return;
    }
    await doSubmit();
  };

  const handleRevoke = async () => {
    if (!lotId || guestBlocked) return;
    setSubmitting(true);
    setError('');
    try {
      // Link público con campañas de Terrio: confirmación antes de apagarlo (todas las unidades del lote).
      if (linkStatus === 'active' && savedAccessMode === 'public') {
        const goOn = await confirmCut({ userLotIds: units.map(getUnitId).filter(Boolean).map(String) }, 'unshare');
        if (!goOn) return;
      }
      if (isSpaceGuest) {
        const res = await unshareUserLot({ lot_id: lotId, ownerUserId: spaceOwner });
        // 404 "Active share not found": ya estaba apagado → mismo estado final.
        if (!res?._httpOk && res?._httpStatus !== 404) {
          setError(
            resolveSpaceGuestWriteError(res?._httpStatus, spaceOwner)
            || readShareApiError(res, 'No se pudo desactivar el link. Intentalo de nuevo.'),
          );
          return;
        }
      } else {
        await unshareUserLot({ lot_id: lotId });
        forgetMetaAutoShare({ scope: 'lot', id: lotId });
      }
      const previousUrl = shareToken ? buildSharedUserLotUrl(shareToken) : shareUrl;
      setShareToken('');
      setLinkStatus(previousUrl ? 'inactive' : 'none');
      setRevokedShareUrl(previousUrl || '');
      setSavedAccessMode(accessMode);
      setOriginalUnitIds([]);
      setOriginalEmails([]);
      setEmails([]);
      setEmailInput('');
      setEmailInputError('');
      setNotifyUsers(false);
      setNotifyMessage('');
      setNotifyMessageError('');
      toast.success('El link fue desactivado');
    } catch (e) {
      setError('No se pudo desactivar el link. Intentalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const canGoNext = !isInitializing && units.length > 0;

  const handleGoNext = () => {
    setError('');
    if (selectedUnitIds.length === 0) {
      setAccessMode('public');
      setEmails([]);
      setOriginalEmails([]);
      setEmailInput('');
      setEmailInputError('');
      setStep(hasShareablePrivateContent ? 'access' : 'lotOnly');
      return;
    }
    setStep('access');
  };

  const handleBackFromShareStep = () => {
    setError('');
    if (lotOnlyMode) return;
    setStep('select');
  };

  const canEditSelection = !lotOnlyMode && units.length > 0 && step === 'access';
  const showLotShareHeader = !isInitializing && step !== 'select';

  const lotShareHeader = (
    <Box sx={SHARE_DIALOG_LIST_META_SX}>
      <Tooltip title={`Lote: ${cadastralLabel}`} placement="top" arrow>
        <Typography
          sx={{
            color: TEXT_PRIMARY,
            fontSize: '16px',
            fontWeight: 500,
            py: 0.25,
            borderRadius: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: '0 1 auto',
            maxWidth: '55%',
          }}
        >
          REGISTRO: {cadastralLabel.toUpperCase()}
        </Typography>
      </Tooltip>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          ml: 'auto',
          flexShrink: 1,
          minWidth: 0,
          maxWidth: '52%',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          sx={{
            color: TEXT_MUTED,
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.35,
            textAlign: 'right',
            whiteSpace: 'normal',
          }}
        >
          {selectionSummary}
        </Typography>
        {canEditSelection ? (
          <Tooltip title="Editar selección" placement="top" arrow>
            <IconButton
              aria-label="Editar selección"
              onClick={handleBackFromShareStep}
              disabled={submitting}
              size="small"
              sx={{
                color: TEXT_PRIMARY,
                p: 1,
                '&:hover': { color: `${colors.primary} !important`, bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );

  const shareLinkSection = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500, mb: -1 }}>
          Enlace
        </Typography>
      </Box>
      <Typography sx={{ color: '#D4D4D4', fontSize: '12px', lineHeight: 1.4 }}>
        {effectiveLinkStatus === 'inactive'
          ? 'El enlace ya no está disponible. Generá uno nuevo para volver a compartir este lote.'
          : accessMode === 'restricted'
            ? 'Solo personas con acceso pueden ver.'
            : effectiveLinkStatus === 'active'
              ? 'Copiá el enlace y compartilo con quien quieras.'
              : 'Todavía no existe un enlace para compartir este lote.'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 44,
          px: 1.25,
          borderRadius: '8px',
          bgcolor: '#242424',
          opacity: effectiveLinkStatus === 'inactive' ? 0.55 : 1,
        }}
      >
        {hasActiveLink ? (
          <LinkIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }} />
        ) : (
          <LinkOffIcon sx={{ fontSize: 18, color: TEXT_MUTED, flexShrink: 0 }} />
        )}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            color: hasActiveLink ? TEXT_PRIMARY : TEXT_MUTED,
            fontSize: '12px',
            fontFamily: typography.fontFamily.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayLinkUrl || 'Generá un enlace para que cualquier persona pueda acceder.'}
        </Typography>
        {displayLinkUrl ? (
          <Button
            size="small"
            onClick={handleCopyLink}
            disabled={effectiveLinkStatus === 'inactive'}
            startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              color: effectiveLinkStatus === 'inactive' ? TEXT_MUTED : colors.primaryLight,
              minWidth: 0,
              px: 1,
              '&:hover': {
                bgcolor: effectiveLinkStatus === 'inactive'
                  ? 'transparent'
                  : 'rgba(72,146,145,0.12)',
              },
              '&.Mui-disabled': { color: TEXT_MUTED, opacity: 1 },
            }}
          >
            Copiar
          </Button>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: SHARE_DIALOG_PAPER_SX }}
      {...brandsOverlap}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2.5,
          py: 1.75,
          flexShrink: 0,
          color: TEXT_PRIMARY,
          fontFamily: typography.fontFamily.heading,
          fontSize: '18px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          Compartir
        </Box>
        <IconButton
          aria-label="Cerrar"
          onClick={handleClose}
          disabled={submitting}
          size="small"
          sx={{
            color: TEXT_SECONDARY,
            '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: BORDER }} />

      {showLotShareHeader ? lotShareHeader : null}

      <DialogContent
        ref={contentRef}
        sx={{
          ...SHARE_DIALOG_CONTENT_SX,
          pt: showLotShareHeader ? 1.5 : 2,
        }}
      >
        {isInitializing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rounded" height={64} sx={skTextSx} />
            <Skeleton variant="rounded" height={56} sx={skTextSx} />
          </Box>
        ) : step === 'lotOnly' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={SHARE_SECTION_SHELL_SX}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500, mb: -1 }}>
                    Enlace para compartir
                  </Typography>
                </Box>
                <Typography sx={{ color: '#D4D4D4', fontSize: '12px', lineHeight: 1.4 }}>
                  {lotOnlyMode
                    ? 'Este lote no tiene unidades funcionales. Cualquier persona con el enlace puede ver la información del lote.'
                    : 'Solo se compartirá la información del lote. Cualquier persona con el enlace puede ver.'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 44,
                    px: 1.25,
                    borderRadius: '8px',
                    bgcolor: '#242424',
                  }}
                >
                  <LinkIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: TEXT_PRIMARY,
                      fontSize: '12px',
                      fontFamily: typography.fontFamily.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {lotOnlyUrl}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleCopyLotOnlyLink}
                    aria-label="Copiar enlace"
                    sx={{
                      flexShrink: 0,
                      color: colors.primaryLight,
                      p: 0.75,
                      '&:hover': { bgcolor: 'rgba(72,146,145,0.12)' },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : step === 'select' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: TEXT_PRIMARY, fontSize: '14px', lineHeight: 1.4 }}>
              Elegí qué información va a ver la otra persona
            </Typography>

            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: '8px',
                overflow: 'hidden',
                bgcolor: BG_ELEVATED,
              }}
            >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      bgcolor: '#1C1C1C',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PlaceOutlinedIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        color: TEXT_PRIMARY,
                        fontSize: '13px',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Número Catastral:{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>{cadastralLabel}</Box>
                    </Typography>
                    <Typography sx={{ color: TEXT_MUTED, fontSize: '12px' }}>
                      {locationLabel}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 1,
                      py: 0.35,
                      borderRadius: '4px',
                      border: `1px solid ${BORDER}`,
                      bgcolor: BG_UNITS,
                    }}
                  >
                    <Typography sx={{ color: CHIP_NONE_COLOR, fontSize: '12px', fontWeight: 500 }}>
                      Siempre incluido
                    </Typography>
                  </Box>
                </Box>

                {units.map((unit, idx) => {
                  const unitId = String(getUnitId(unit) || '');
                  if (!unitId) return null;
                  const checked = selectedUnitIds.includes(unitId);
                  const meta = formatUnitMeta(unit);
                  const isLast = idx === units.length - 1;
                  return (
                    <Box
                      key={unitId}
                      onClick={() => toggleUnit(unitId)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1.1,
                        bgcolor: BG_UNITS,
                        borderTop: `1px solid ${BORDER}`,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#252525' },
                        borderBottomLeftRadius: isLast ? '8px' : 0,
                        borderBottomRightRadius: isLast ? '8px' : 0,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleUnit(unitId)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          p: 0.5,
                          color: 'rgba(255,255,255,0.35)',
                          '&.Mui-checked': { color: colors.primary },
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            color: TEXT_PRIMARY,
                            fontSize: '13px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getUnitDisplayTitle(unit)}
                        </Typography>
                        {meta ? (
                          <Typography sx={{ color: TEXT_MUTED, fontSize: '12px' }}>
                            {meta}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

            {!!error && (
              <Typography sx={{ color: DANGER, fontSize: '12px' }}>{error}</Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
                Acceso
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <AccessModeCard
                  selected={accessMode === 'public'}
                  disabled={submitting}
                  icon={<LanguageIcon sx={{ fontSize: 18 }} />}
                  title="Público"
                  description="Cualquier persona con el enlace puede ver, sin iniciar sesión."
                  onClick={() => {
                    setAccessMode('public');
                    setError('');
                  }}
                />
                <AccessModeCard
                  selected={accessMode === 'restricted'}
                  disabled={submitting}
                  icon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
                  title="Restringido"
                  description="Solo personas invitadas pueden acceder."
                  onClick={() => {
                    setAccessMode('restricted');
                    setError('');
                  }}
                />
              </Box>
            </Box>

            {isPrivate ? (
              <Box sx={SHARE_SECTION_SHELL_SX}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500, mb: -1 }}>
                    Personas
                  </Typography>
                  <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4 }}>
                    Añade los correos electrónicos de las personas que deseas invitar.
                  </Typography>
                  <Box
                    onClick={() => inputRef.current?.focus()}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minHeight: 44,
                      px: 1.5,
                      py: 1,
                      borderRadius: '8px',
                      border: 'none',
                      bgcolor: '#242424',
                      cursor: 'text',
                      transition: 'box-shadow 0.15s',
                      '&:focus-within': {
                        boxShadow: emailInputError
                          ? '0 0 0 1px rgba(252,165,165,0.70)'
                          : `0 0 0 1px ${colors.primary}`,
                      },
                      '& input': SHARE_EMAIL_INPUT_SX,
                    }}
                  >
                    <MailOutlineIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }} />
                    <input
                      ref={inputRef}
                      value={emailInput}
                      onChange={handleEmailChange}
                      onKeyDown={handleEmailKeyDown}
                      onBlur={handleEmailBlur}
                      placeholder="correo@ejemplo.com.ar"
                      autoComplete="email"
                    />
                  </Box>
                  {emailInputError && (
                    <Typography sx={{ color: DANGER, fontSize: '11px', ml: 0.5 }}>
                      {emailInputError}
                    </Typography>
                  )}
                  {emails.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      <Box sx={INVITE_LIST_SX}>
                        {emails.map((email) => (
                          <Box
                            key={email}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                              minHeight: 40,
                              px: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: colors.primary,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: '11px',
                                fontWeight: 700,
                                fontFamily: typography.fontFamily.primary,
                                letterSpacing: '0.02em',
                              }}
                              aria-hidden
                            >
                              {getEmailInitials(email)}
                            </Box>
                            <Typography
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                color: TEXT_PRIMARY,
                                fontSize: '13px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {email}
                            </Typography>
                            <Typography
                              sx={{
                                color: TEXT_SECONDARY,
                                fontSize: '12px',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {originalEmails.includes(email) ? 'Puede ver' : 'Pendiente'}
                            </Typography>
                            <Tooltip title="Quitar acceso" placement="top" arrow>
                              <IconButton
                                aria-label={`Quitar acceso a ${email}`}
                                size="small"
                                disabled={submitting}
                                onClick={() => handleRemoveInvitee(email)}
                                sx={{
                                  color: TEXT_SECONDARY,
                                  flexShrink: 0,
                                  p: 0.5,
                                  '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          mt: 0.5,
                          pt: 1,
                          borderTop: `1px solid ${BORDER}`,
                        }}
                      >
                        <Checkbox
                          checked={notifyUsers}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setNotifyUsers(checked);
                            if (!checked) setNotifyMessageError('');
                          }}
                          disabled={submitting}
                          size="small"
                          sx={{
                            p: 0,
                            ml: 0,
                            color: TEXT_SECONDARY,
                            '&.Mui-checked': { color: colors.primary },
                          }}
                        />
                        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px' }}>
                          Notificar a los usuarios
                        </Typography>
                      </Box>

                      {notifyUsers && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                          <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 500 }}>
                            Mensaje
                          </Typography>
                          <Box
                            component="textarea"
                            value={notifyMessage}
                            onChange={(e) => {
                              setNotifyMessage(e.target.value.slice(0, 2000));
                              if (notifyMessageError) setNotifyMessageError('');
                            }}
                            placeholder="Escribí un mensaje para los nuevos invitados"
                            rows={3}
                            maxLength={2000}
                            disabled={submitting}
                            sx={{
                              width: '100%',
                              boxSizing: 'border-box',
                              resize: 'vertical',
                              minHeight: 72,
                              px: 1.5,
                              py: 1.25,
                              borderRadius: '8px',
                              border: `1px solid ${notifyMessageError ? DANGER : BORDER}`,
                              bgcolor: '#242424',
                              color: TEXT_PRIMARY,
                              fontFamily: typography.fontFamily.primary,
                              fontSize: '13px',
                              lineHeight: 1.4,
                              outline: 'none',
                              opacity: submitting ? 0.7 : 1,
                              '&::placeholder': { color: TEXT_MUTED },
                              '&:focus': {
                                borderColor: notifyMessageError ? DANGER : colors.primary,
                              },
                            }}
                          />
                          {!!notifyMessageError && (
                            <Typography sx={{ color: DANGER, fontSize: '12px' }}>
                              {notifyMessageError}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : null}

            <Box sx={SHARE_SECTION_SHELL_SX}>
              {shareLinkSection}
            </Box>

            <ShareTemplateSection
              value={shareTemplate}
              brandId={brandId}
              onChange={(nextTemplate, nextBrandId) => {
                setShareTemplate(nextTemplate);
                setBrandId(nextBrandId);
              }}
              disabled={submitting}
              locked={isSpaceGuest}
            />

            {!!error && (
              <Typography sx={{ color: DANGER, fontSize: '12px' }}>{error}</Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          justifyContent: 'flex-end',
          ...getShareDialogFooterScrollHintSx(canScrollMore),
        }}
      >
        {isInitializing ? (
          <Skeleton variant="rounded" width={140} height={40} sx={skFooterButtonSx} />
        ) : step === 'lotOnly' ? (
          <Button
            variant="contained"
            onClick={handleCopyLotOnlyLinkAndClose}
            disabled={!lotOnlyUrl}
            startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
            sx={getSharePrimaryButtonSx()}
          >
            Copiar enlace
          </Button>
        ) : step === 'select' ? (
          <>
            <Button
              onClick={handleClose}
              disabled={submitting}
              variant="outlined"
              sx={getShareCancelButtonSx({ busy: submitting })}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleGoNext}
              disabled={!canGoNext || submitting}
              sx={getSharePrimaryButtonSx({ busy: submitting && canGoNext })}
            >
              Siguiente
            </Button>
          </>
        ) : (
          <>
            {isPrivate ? (
              <>
                {hasActiveLink && (
                  <Button
                    onClick={handleRevoke}
                    disabled={submitting || guestBlocked}
                    variant="outlined"
                    startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
                    sx={getShareDangerButtonSx({ busy: submitting })}
                  >
                    Desactivar enlace
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || privatePrimaryDisabled || guestBlocked}
                  sx={getSharePrimaryButtonSx({
                    busy: submitting && !privatePrimaryDisabled,
                  })}
                >
                  {privatePrimaryLabel}
                </Button>
              </>
            ) : hasActiveLink ? (
              <>
                <Button
                  onClick={handleRevoke}
                  disabled={submitting || guestBlocked}
                  variant="outlined"
                  startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
                  sx={getShareDangerButtonSx({ busy: submitting })}
                >
                  Desactivar enlace
                </Button>
                {(unitsDirty || templateDirty) && (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || guestBlocked}
                    sx={getSharePrimaryButtonSx({ busy: submitting })}
                  >
                    Actualizar
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || guestBlocked}
                startIcon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
                sx={getSharePrimaryButtonSx({ busy: submitting })}
              >
                Generar enlace
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
    {guardDialog}
    </>
  );
}
